import { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function stripBBCode(input) {
  return input.replace(/\[.*?\]/g, '').replace(/<.*?>/g, '');
}

function highlight(text, term) {
  if (!term) return text;
  const re = new RegExp(`(${term})`, 'gi');
  return text.split(re).map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark
        key={i}
        className="bg-yellow-300 px-1 rounded text-black dark:text-black"
      >
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function SearchView() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sort, setSort] = useState('relevance');

  const { user } = useAuth();

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const search = async (reset = true) => {
    if (!query.trim() || cooldown > 0) return;
    setLoading(true);
    const currentPage = reset ? 1 : page + 1;
    try {
      const res = await axiosInstance.get(`/search?q=${encodeURIComponent(query)}&page=${currentPage}&sort=${sort}`);
      const newResults = res.data.results || [];
      setResults(reset ? newResults : [...results, ...newResults]);
      setPage(currentPage);
      setHasMore(res.data.hasMore);
      setCooldown(30);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4">
      <div className="text-center mb-12">
        <h1 className="text-5xl sm:text-6xl text-white font-marker mb-4 animate-fade-in">
          Suche
        </h1>
        <p className="text-xl text-gray-300 font-light">
          Durchsuche alle Threads und Beiträge
        </p>
      </div>

      <div className="modern-card p-6 mb-8">
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="flex-1 w-full">
            <input
              type="text"
              className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search(true)}
              placeholder="Suchbegriff eingeben..."
            />
          </div>
          <div className="flex gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200"
            >
              <option value="relevance">Relevanz</option>
              <option value="newest">Neueste zuerst</option>
              <option value="oldest">Älteste zuerst</option>
            </select>
            <button
              onClick={() => search(true)}
              disabled={cooldown > 0 || loading}
              className="modern-btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Suche...
                </span>
              ) : (
                'Suchen'
              )}
            </button>
          </div>
        </div>
        {cooldown > 0 && (
          <div className="text-sm text-gray-300 mt-2 text-center">
            Nächste Suche in {cooldown}s möglich ⏳
          </div>
        )}
      </div>

      {results.length > 0 ? (
        <div className="space-y-6">
          {results.map((post, index) => (
            <div
              key={post._id}
              className="modern-card group hover:scale-[1.02] transition-all duration-300"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    {formatDate(post.createdAt)}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    von {post.author?.username || '—'}
                  </span>
                </div>
                <Link
                  to={`/thread/${post.thread?._id || post.thread}`}
                  className="block text-xl font-semibold hover:text-primary transition-colors duration-200 text-gray-800 dark:text-white group-hover:text-primary mb-3"
                >
                  {highlight(post.thread?.title || 'Kein Titel', query)}
                </Link>
                <div className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {highlight(stripBBCode(post.snippet || ''), query)}
                </div>
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => search(false)}
                disabled={loading || cooldown > 0}
                className="modern-btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Lade weitere...
                  </span>
                ) : (
                  'Weitere laden'
                )}
              </button>
            </div>
          )}
        </div>
      ) : query && !loading && (
        <div className="modern-card p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400 text-lg">
            Keine Ergebnisse für "{query}" gefunden.
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchView;